// Validação do endereço do DESTINATÁRIO da NF-e (grupo dest/enderDest).
//
// Estes campos NÃO são "endereço de transporte": eles viram o dest/enderDest do XML, que é
// obrigatório em qualquer modalidade de frete — inclusive na retirada pelo cliente (modFrete 9).
//
// A lista espelha EXATAMENTE o `validate:"required"` de CreateBlingInvoiceRequest
// (ERPApi/internal/core/domain/dtos.go): Logradouro, Bairro, Cidade, UF e CEP. Número e
// Complemento ficam de fora porque o builder da NF-e emite "S/N" quando o número vem vazio
// (ERPApi/pkg/sefaz/nfe_build.go, writeEnder) — exigi-los aqui barraria notas que a SEFAZ aceita.
//
// Pura para ser testável sem renderizar o formulário (mesmo padrão de paymentPlan.ts).

export interface DestAddress {
  street: string;
  district: string;
  city: string;
  state: string;
  zip: string;
}

// A ordem segue a do formulário, para o operador achar o campo que falta.
const REQUIRED_DEST_FIELDS: { key: keyof DestAddress; label: string }[] = [
  { key: 'street', label: 'Logradouro' },
  { key: 'district', label: 'Bairro' },
  { key: 'city', label: 'Cidade' },
  { key: 'state', label: 'UF' },
  { key: 'zip', label: 'CEP' },
];

/** Rótulos (pt-BR) dos campos obrigatórios do destinatário que estão vazios. */
export function missingDestAddressFields(addr: DestAddress): string[] {
  return REQUIRED_DEST_FIELDS
    .filter(f => !(addr[f.key] ?? '').trim())
    .map(f => f.label);
}

/** Mensagem de bloqueio, no estilo das demais mensagens do formulário. */
export function destAddressErrorMessage(missing: string[]): string | null {
  if (missing.length === 0) return null;
  return (
    'Complete o endereço do destinatário da nota (obrigatório inclusive na retirada pelo cliente): ' +
    `${missing.join(', ')}. Informe o CEP para preencher os demais campos automaticamente.`
  );
}

/** Resposta do ViaCEP, no formato que o hook useCepSearch já devolve. */
export interface CepAddressLookup {
  logradouro: string;
  bairro: string;
  localidade: string;
  uf: string;
}

/**
 * Completa APENAS os campos vazios do endereço do destinatário com o que veio do ViaCEP.
 *
 * NUNCA sobrescreve valor já preenchido: o endereço da nota é dado fiscal, e o que veio do
 * pedido ou do cadastro do cliente tem precedência sobre a base de CEP — que não conhece número
 * nem complemento e cujo logradouro pode divergir do gravado (ex.: o ponto de retirada tem
 * "Rua Aureliano Guimarães, 150 sl 912" gravado como logradouro, e o ViaCEP devolve só
 * "Rua Aureliano Guimarães").
 *
 * Devolve só os campos efetivamente completados, para a tela poder dizer ao operador o que
 * mudou — preenchimento silencioso de campo fiscal não é aceitável.
 */
export function fillMissingDestAddress(current: DestAddress, cep: CepAddressLookup): Partial<DestAddress> {
  const patch: Partial<DestAddress> = {};
  const fill = (key: keyof DestAddress, value: string) => {
    if ((current[key] ?? '').trim()) return; // já preenchido: não toca
    const clean = (value ?? '').trim();
    if (clean) patch[key] = clean;
  };
  fill('street', cep.logradouro);
  fill('district', cep.bairro);
  fill('city', cep.localidade);
  fill('state', cep.uf);
  return patch;
}

/** Rótulos (pt-BR) dos campos completados pelo ViaCEP, na ordem do formulário. */
export function filledDestAddressLabels(patch: Partial<DestAddress>): string[] {
  return REQUIRED_DEST_FIELDS.filter(f => patch[f.key] !== undefined).map(f => f.label);
}

/** Tamanho máximo dos textos do endereço do destinatário no schema da NF-e (xLgr, nro, xCpl, xBairro, xMun). */
export const NFE_ADDRESS_MAX_LEN = 60;

/**
 * Tamanho do texto como sai no XML: o backend apara as pontas e colapsa espaços repetidos
 * (normalizeNFeText) e o schema conta caracteres, não bytes. Espelha `sefaz.NFeTextLen`.
 */
export function nfeTextLength(value: string | undefined | null): number {
  return Array.from((value ?? '').trim().split(/\s+/).filter(Boolean).join(' ')).length;
}

/**
 * Campos do endereço acima do limite da NF-e. O `maxLength` do input não corta valor que já veio
 * do pedido (caso do pedido 29954: instrução de entrega digitada no logradouro, 75 caracteres,
 * rejeitada pela SEFAZ com cStat 225), então a tela precisa avisar e bloquear (RN-NFE-73).
 */
export function destAddressTooLong(fields: { label: string; value: string }[]): { label: string; length: number }[] {
  return fields
    .map(f => ({ label: f.label, length: nfeTextLength(f.value) }))
    .filter(f => f.length > NFE_ADDRESS_MAX_LEN);
}
